import type { ChangeEvent } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import "../../styles/field.css";
import "./index.css";
import { clsx } from "clsx";
import { mergeRefs } from "../../utils";
import type { ITextareaProps } from "./types";

// Where `field-sizing: content` works, CSS grows the textarea and no script runs.
const supportsFieldSizing =
  typeof CSS !== "undefined" && CSS.supports("field-sizing", "content");

const fitHeight = (element: HTMLTextAreaElement) => {
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
};

export const Textarea = (props: ITextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uncontrolledLength, setUncontrolledLength] = useState<number>(
    () => String(props.defaultValue ?? "").length,
  );
  // A controlled value counts from the first render; only uncontrolled typing needs state.
  const length =
    props.value !== undefined ? String(props.value).length : uncontrolledLength;

  const nativeProps: ITextareaProps = useMemo(() => {
    const nativeProps: ITextareaProps = { ...props };
    delete nativeProps.invalid;
    delete nativeProps.autosize;
    delete nativeProps.ref;

    return nativeProps;
  }, [props]);

  const rootClass: string = clsx(
    "nuco-field nuco-textarea",
    props.disabled && "nuco-field--disabled",
    props.readOnly && "nuco-field--readOnly",
    props.invalid && "nuco-field--invalid nuco-color-danger",
    props.autosize && "nuco-textarea--autosize",
    props.className,
  );

  // Layout effect: the first paint already has the full height.
  useLayoutEffect(() => {
    const element = textareaRef.current;
    if (!props.autosize || supportsFieldSizing || !element) return;

    fitHeight(element);
    // Re-measure when the web font swaps in or the width changes the wrapping.
    let width = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      fitHeight(element);
    });
    observer.observe(element);
    document.fonts?.ready.then(() => fitHeight(element));

    return () => observer.disconnect();
  }, [props.autosize, props.value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setUncontrolledLength(e.currentTarget.value.length);
    props.onChange?.(e.currentTarget.value, e);
  };

  return (
    <div className={rootClass}>
      <textarea
        {...nativeProps}
        ref={mergeRefs(textareaRef, props.ref)}
        className="nuco-field__control nuco-textarea__textarea nuco-text nuco-text--md"
        onChange={handleChange}
      />
      {props.maxLength !== undefined && !props.disabled && (
        <div className="nuco-textarea__length nuco-text nuco-text--sm">
          {length}/{props.maxLength}
        </div>
      )}
    </div>
  );
};
